import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Calendar, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function HeroCarousel({ banners, liveBookingShows }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Combine live booking shows with banners or use live booking shows directly
  const slides = React.useMemo(() => {
    const list = [];

    // Prioritize live booking shows
    if (liveBookingShows && liveBookingShows.length > 0) {
      liveBookingShows.forEach((show, idx) => {
        list.push({
          id: `show-${show.product_id || show.id || idx}`,
          image: show.image || show.hover_image || (banners && banners[idx % banners.length]?.image) || '',
          title: show.jatra_name || 'Jatra Show',
          party: show.cat_name || 'Gananatya',
          date: show.jatra_date,
          time: show.jatra_time,
          location: show.jatra_location || 'Odisha',
          productId: show.product_id || show.id,
          isLive: true
        });
      });
    }

    // Include additional banners if present
    if (banners && banners.length > 0) {
      banners.forEach((b, idx) => {
        // Only add if not already matched
        if (!list.some(s => s.image === b.image)) {
          list.push({
            id: `banner-${b.id || idx}`,
            image: b.image,
            title: b.title || 'Lokanatya Jatra',
            party: b.cat_name || 'Opera Group',
            date: b.date || '2026-08-15',
            time: b.time || '11:00 PM',
            location: b.location || 'Bhubaneswar',
            productId: b.product_id || (liveBookingShows && liveBookingShows[0]?.product_id) || null,
            isLive: false
          });
        }
      });
    }

    // Fallback if empty
    if (list.length === 0) {
      return [
        {
          id: 'default-1',
          image: 'https://lokanatya.com/logo.png',
          title: 'Lokanatya Jatra',
          party: 'Gananatya',
          date: '2026-08-15',
          time: '11:00 PM',
          location: 'Bhubaneswar',
          productId: null,
          isLive: true
        }
      ];
    }

    return list;
  }, [banners, liveBookingShows]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play interval
  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [slides]);

  if (!slides || slides.length === 0) return null;

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '15 Aug 2026';
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj)) return dateStr;
    return dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Calculate track position to keep active banner perfectly centered in view
  const translateX = isMobile
    ? `-${currentSlide * 100}%`
    : `calc(11% - ${currentSlide * 78}% - ${currentSlide * 1}rem)`;

  return (
    <div className="w-full overflow-hidden bg-white pb-2 md:py-2">
      <div className="max-w-7xl mx-auto relative px-0 md:px-4">

        {/* Carousel Track Container */}
        <div className="relative w-full overflow-hidden">
          <motion.div
            className="flex gap-0 md:gap-4 cursor-grab active:cursor-grabbing items-center"
            animate={{ x: translateX }}
            transition={{
              duration: 0.55,
              ease: [0.25, 0.1, 0.25, 1]
            }}
            style={{ touchAction: 'pan-y' }}
          >
            {slides.map((slide, index) => {
              const isActive = index === currentSlide;
              const bookingLink = slide.productId ? `/shows/${slide.productId}/seats` : '/shows';

              return (
                <div
                  key={slide.id || index}
                  onClick={() => setCurrentSlide(index)}
                  className="w-full flex-shrink-0 md:w-[78%]"
                >
                  <motion.div
                    initial={false}
                    animate={{
                      opacity: isActive ? 1 : 0.42,
                      scale: isActive ? 1 : 0.93,
                      y: isActive ? [-2, 0] : 0
                    }}
                    transition={{
                      opacity: { duration: 0.45, ease: "easeOut" },
                      scale: { duration: 0.45, ease: "easeOut" },
                      y: { duration: 0.45, ease: "easeOut" }
                    }}
                    className="relative w-full overflow-hidden rounded-none md:rounded-2xl aspect-[2.4/1] sm:aspect-[2.4/1] md:aspect-[21/8] bg-gray-900 shadow-xs md:shadow-md border-0 cursor-pointer group"
                  >
                    {/* Banner Image - Seamless Edge-to-Edge */}
                    <img
                      src={slide.image || ''}
                      alt={slide.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-102"
                      draggable="false"
                      onError={(e) => {
                        e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg';
                      }}
                    />

                    {/* Dark Gradient Overlay & Live Booking Details - Only render if slide has live booking data */}
                    {slide.isLive && slide.productId && (
                      <div className="absolute inset-0 flex bg-gradient-to-t md:bg-gradient-to-l from-[#1b1523]/95 via-[#1b1523]/70 to-transparent flex-col justify-end md:justify-center p-4 md:p-10 text-white pointer-events-none">
                        <div className="flex flex-col gap-2 md:gap-4 ml-auto mr-0 md:mr-4 lg:mr-8 max-w-full md:max-w-[300px] pointer-events-auto bg-[#1b1523]/80 md:bg-transparent p-3 md:p-0 rounded-2xl backdrop-blur-md md:backdrop-blur-none border border-white/10 md:border-none">
                          
                          {/* Live Booking Badge */}
                          <div className="flex items-center gap-2">
                            <span className="bg-[#ffebec] text-[#d33a33] text-[9px] font-extrabold px-2 py-0.5 rounded-full flex items-center border border-[#ffd2d4]">
                              <span className="w-1.5 h-1.5 bg-[#d33a33] rounded-full mr-1 animate-pulse"></span>
                              LIVE BOOKING
                            </span>
                            <span className="text-[10px] font-bold text-red-300 uppercase tracking-widest truncate">
                              {slide.party}
                            </span>
                          </div>

                          {/* Show Name */}
                          <h3 className="text-sm md:text-lg font-extrabold text-white leading-snug line-clamp-1">
                            {slide.title}
                          </h3>

                          {/* Starts From Date */}
                          {slide.date && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-red-500/20 text-red-400 flex items-center justify-center shrink-0">
                                <Calendar size={16} />
                              </div>
                              <div>
                                <p className="text-[10px] text-red-400 font-bold uppercase tracking-wider leading-none">Starts From</p>
                                <p className="text-xs md:text-sm font-bold text-white leading-tight mt-0.5">{formatDate(slide.date)}</p>
                              </div>
                            </div>
                          )}

                          {/* Location Detail */}
                          {slide.location && (
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                                <MapPin size={16} />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-200 leading-snug truncate">{slide.location}</p>
                              </div>
                            </div>
                          )}

                          {/* Book Tickets Button */}
                          <Link
                            to={bookingLink}
                            className="mt-1 md:mt-2 w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-bold py-2 px-4 md:py-2.5 md:px-5 rounded-xl text-xs shadow-lg shadow-red-500/30 flex items-center justify-center gap-2 transition transform hover:scale-102 active:scale-98"
                          >
                            <Sparkles size={14} />
                            <span>Book Tickets Now</span>
                          </Link>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </motion.div>

          {/* Left Arrow Button */}
          <button
            onClick={handlePrev}
            className="absolute left-3 top-1/2 z-20 hidden -translate-y-1/2 md:flex h-10 w-10 items-center justify-center rounded-full bg-black/50 hover:bg-black/75 text-white shadow-xl backdrop-blur-sm transition-colors border border-white/10"
            aria-label="Previous Slide"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Right Arrow Button */}
          <button
            onClick={handleNext}
            className="absolute right-3 top-1/2 z-20 hidden -translate-y-1/2 md:flex h-10 w-10 items-center justify-center rounded-full bg-black/50 hover:bg-black/75 text-white shadow-xl backdrop-blur-sm transition-colors border border-white/10"
            aria-label="Next Slide"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Ama Jatra Style Dot Indicators */}
        <div className="mt-2 flex justify-center items-center gap-1.5">
          {slides.map((_, index) => {
            const isActive = index === currentSlide;
            return (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`relative h-1.5 overflow-hidden rounded-full transition-all duration-350 ${isActive ? 'w-7 bg-red-600' : 'w-1.5 bg-gray-300 hover:bg-gray-400 opacity-60'
                  }`}
                aria-label={`Slide ${index + 1}`}
              />
            );
          })}
        </div>

      </div>
    </div>
  );
}
